import {
  formatearMoneda,
  normalizarDecimal,
  normalizarMoneda,
} from './numeros'

// Cálculos de Registro de compra por pesaje.
//
// Los kilos se operan como ENTEROS en milésimas de kg (gramos) para que
// sumar 18 pesajes como 125,5 + 119,8 + ... dé exactamente lo mismo que
// el `numeric(16,3)` de Supabase, sin el arrastre de float64 (0,1 + 0,2).
// La conversión a milésimas se hace desde el texto decimal, no con
// Math.round: nunca se descartan decimales en silencio; un valor con más
// de 3 decimales simplemente se rechaza como inválido.
//
// Supabase (RPC registrar_compra) recalcula tara, neto y total: lo de
// acá es solo la vista previa en vivo, con la misma fórmula.

const PATRON_DECIMAL_TEXTO = /^(\d+)(?:\.(\d+))?$/

// Convierte un número/texto decimal (ya normalizado con punto) a entero
// escalado por 10^decimales. Devuelve NaN si tiene más decimales de los
// permitidos.
function aEnteroEscalado(valor, decimales) {
  const texto =
    typeof valor === 'number' ? String(valor) : String(valor ?? '')

  const coincidencia = PATRON_DECIMAL_TEXTO.exec(texto)

  if (!coincidencia) return NaN

  const [, entero, fraccion = ''] = coincidencia

  if (fraccion.length > decimales) return NaN

  return (
    Number(entero) * 10 ** decimales +
    Number(fraccion.padEnd(decimales, '0') || 0)
  )
}

// Peso físico ingresado por el usuario ("125,5", "125.5") -> milésimas.
// Usa normalizarDecimal (coma o punto = decimal), NUNCA normalizarMoneda.
export function pesoAMilesimas(valor) {
  const numero = normalizarDecimal(valor)

  if (!Number.isFinite(numero) || numero < 0) return NaN

  return aEnteroEscalado(numero, 3)
}

export const PESO_PALLET_MAXIMO_MILESIMAS = 10000 * 1000

// Peso del pallet ingresado por pesaje (kg). Vacío = 0. Acepta coma o punto
// decimal, hasta 3 decimales, sin negativos y hasta 10.000 kg. NaN si no es válido.
export function pesoPalletAMilesimas(valor) {
  if (String(valor ?? '').trim() === '') return 0

  const milesimas = pesoAMilesimas(valor)

  return milesimas <= PESO_PALLET_MAXIMO_MILESIMAS ? milesimas : NaN
}

// Tara del maestro tipos_envase (numeric(10,3) llega como número JSON).
export function taraAMilesimas(valor) {
  const numero = Number(valor)

  if (!Number.isFinite(numero) || numero < 0) return NaN

  return aEnteroEscalado(numero, 3)
}

// Precio/kg ingresado ("750", "2.500", "$12.333", "750,50") -> centavos.
// Usa normalizarMoneda (punto = miles, coma = decimal).
export function precioACentavos(valor) {
  const numero = normalizarMoneda(valor)

  if (!Number.isFinite(numero) || numero < 0) return NaN

  return aEnteroEscalado(numero, 2)
}

export function milesimasAKilos(milesimas) {
  return milesimas / 1000
}

export function centavosAPesos(centavos) {
  return centavos / 100
}

// Cantidad de cajas: entero > 0 escrito tal cual (sin decimales ni
// separadores). Devuelve NaN si no cumple.
export function normalizarCantidadCajas(valor) {
  const texto = String(valor ?? '').trim()

  if (!/^\d+$/.test(texto)) return NaN

  const numero = Number(texto)

  return numero > 0 && numero <= 100000 ? numero : NaN
}

// tara_total = cajas * tara_unitaria + peso_pallet (ingresado por pesaje,
// descontado una sola vez) ; peso_neto = bruto - tara_total
export function calcularPesaje({
  cantidadCajas,
  taraUnitariaMilesimas,
  pesoBrutoMilesimas,
  taraPalletMilesimas = 0,
}) {
  const taraCajasMilesimas = cantidadCajas * taraUnitariaMilesimas
  const taraTotalMilesimas = taraCajasMilesimas + taraPalletMilesimas

  return {
    taraCajasMilesimas,
    taraPalletMilesimas,
    taraTotalMilesimas,
    pesoNetoMilesimas: pesoBrutoMilesimas - taraTotalMilesimas,
  }
}

// total_compra = round(peso_neto_total * precio_kg, 2), redondeo
// "half away from zero" igual que round(numeric, 2) de PostgreSQL. Todo
// en enteros: milésimas * centavos = 1e-5 pesos; se lleva a centavos.
export function calcularTotalCompraCentavos(
  pesoNetoMilesimas,
  precioCentavos
) {
  const producto = pesoNetoMilesimas * precioCentavos
  const cociente = Math.floor(producto / 1000)
  const resto = producto - cociente * 1000

  return cociente + (resto >= 500 ? 1 : 0)
}

export function calcularResumenCompra(pesajes, precioCentavos) {
  const resumen = pesajes.reduce(
    (acumulado, pesaje) => ({
      cantidadPesajes: acumulado.cantidadPesajes + 1,
      cantidadCajas: acumulado.cantidadCajas + pesaje.cantidadCajas,
      pesoBrutoMilesimas:
        acumulado.pesoBrutoMilesimas + pesaje.pesoBrutoMilesimas,
      taraTotalMilesimas:
        acumulado.taraTotalMilesimas + pesaje.taraTotalMilesimas,
      pesoNetoMilesimas:
        acumulado.pesoNetoMilesimas + pesaje.pesoNetoMilesimas,
    }),
    {
      cantidadPesajes: 0,
      cantidadCajas: 0,
      pesoBrutoMilesimas: 0,
      taraTotalMilesimas: 0,
      pesoNetoMilesimas: 0,
    }
  )

  const precioValido = Number.isFinite(precioCentavos)
    ? precioCentavos
    : 0

  return {
    ...resumen,
    totalCompraCentavos: calcularTotalCompraCentavos(
      resumen.pesoNetoMilesimas,
      precioValido
    ),
  }
}

// Kilos con al menos 1 decimal y hasta 3 ("2.102,0 kg", "113,5 kg",
// "126,25 kg"). No redondea: los valores ya vienen con máx. 3 decimales.
export function formatearKilosCompra(valor) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return '0,0 kg'

  return `${numero.toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 3,
  })} kg`
}

export function formatearMontoCompra(valor) {
  return formatearMoneda(valor)
}

export function formatearEnteroCompra(valor) {
  return (Number(valor) || 0).toLocaleString('es-CL')
}

export const ESTADOS_COMPRA = {
  registrada: {
    etiqueta: 'Registrada',
    clase: 'bg-emerald-100 text-emerald-700',
  },
  pendiente_recepcion: {
    etiqueta: 'Pendiente de recepción',
    clase: 'bg-amber-100 text-amber-700',
  },
  anulada: {
    etiqueta: 'Anulada',
    clase: 'bg-red-50 text-red-600',
  },
}

export function obtenerEstadoCompra(valor) {
  return ESTADOS_COMPRA[valor] || ESTADOS_COMPRA.registrada
}

export function nombreProveedorCompra(proveedor) {
  if (!proveedor) return '—'

  const razonSocial =
    proveedor.razon_social || proveedor.proveedor_razon_social
  const nombreFantasia =
    proveedor.nombre_fantasia || proveedor.proveedor_nombre_fantasia

  if (
    nombreFantasia &&
    razonSocial &&
    nombreFantasia.trim().toLowerCase() !==
      razonSocial.trim().toLowerCase()
  ) {
    return `${razonSocial} (${nombreFantasia})`
  }

  return razonSocial || nombreFantasia || '—'
}

// Mismo criterio que el backend (anular_compra): solo compras
// registradas, y solo quien la registró o un administrador. Supabase
// sigue siendo la autoridad final.
export function puedeAnularCompra(compra, { esAdministrador, esConsulta, usuarioId }) {
  if (!compra || esConsulta) return false
  if (!['registrada', 'pendiente_recepcion'].includes(compra.estado)) {
    return false
  }
  if (esAdministrador) return true

  return Boolean(usuarioId) && compra.creado_por === usuarioId
}

// Eliminación definitiva: solo administradores. El backend vuelve a
// verificar permisos y dependencias.
export const MOTIVO_ELIMINACION_MIN = 10
export const MOTIVO_ELIMINACION_MAX = 500

export function puedeEliminarCompra(rol) {
  return rol === 'administrador'
}

// Valida lo que el usuario escribe en el modal de eliminación. El código
// debe coincidir exactamente (sin recortar ni ignorar mayúsculas).
export function validarEliminacionCompra({
  preview,
  codigo,
  motivo,
}) {
  const motivoLimpio = String(motivo ?? '').trim()
  const errores = {}

  if (motivoLimpio.length < MOTIVO_ELIMINACION_MIN) {
    errores.motivo = `El motivo debe tener al menos ${MOTIVO_ELIMINACION_MIN} caracteres.`
  } else if (motivoLimpio.length > MOTIVO_ELIMINACION_MAX) {
    errores.motivo = `El motivo no puede superar ${MOTIVO_ELIMINACION_MAX} caracteres.`
  }

  if (!preview?.codigo_compra || codigo !== preview.codigo_compra) {
    errores.codigo = 'El código debe coincidir exactamente con el de la compra.'
  }

  return {
    motivoLimpio,
    errores,
    puedeConfirmar:
      preview?.puede_eliminar === true &&
      Object.keys(errores).length === 0,
  }
}
